import React, { useState } from 'react';

import Input from '../Input/Input';
import Button from '../Button/Button';
import Div from '../Element/Div/Div';
import Link from '../Link/Link';
import Gutter from '../Gutter/Gutter';
import BodyText from '../Typography/BodyText/BodyText';
import Icon from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';
import InlineNotification from '../InlineNotification/InlineNotification';
import Flex from '../Flex/Flex';

import styles from './CredentialsLogin.module.scss';

type CredentialsLoginProps = {
  /** Description for login type */
  description?: string;
  /** Password object */
  password: {
    /** Password placeholder */
    placeholder?: string;
    /** Password tooltip */
    tooltip?: string;
    /** Password reset link object */
    reset?: {
      /** Password reset url */
      url: string;
      /** Password reset label */
      label: string;
    };
    /** Password value */
    value: string;
    /** Password onChange callback */
    onChange: (event: string) => void;
    /** Password label */
    label: string;
  };
  /** Username object */
  username: {
    /** username placeholder */
    placeholder?: string;
    /** Username label */
    label: string;
    /** Username value */
    value: string;
    /** Username onChange callback */
    onChange: (event: string) => void;
  };
  /** Login button object */
  loginButton: {
    /** Login button label */
    label: string;
    /** Login button onClick callback */
    onClick: () => void;
  };
  /** Strong authentication login suggestion */
  strongAuthentication?: {
    /** Text above the strong authentication link */
    title?: string;
    /** Strong authentication link text */
    label: string;
    /** Strong authehtication url */
    url: string;
  };
  /** Register link object */
  register?: {
    /** Register label */
    label: string;
    /** Register url */
    url: string;
  };
  /** Error message is shown above the form to indicate an erroneous state */
  errorMessage?: string;
  /** Highlighted Info text is shown below the login button */
  highlightedInfoText?: string;
};

const CredentialsLogin: React.FunctionComponent<CredentialsLoginProps> = ({
  description,
  password,
  username,
  loginButton,
  strongAuthentication,
  register,
  errorMessage,
  highlightedInfoText,
}) => {
  const [loading, setLoading] = useState(false);
  const [showInlineNotification, setShowInlineNotification] = useState(true);

  return (
    <Div padding={{ top: 'lg' }}>
      {description && (
        <>
          <BodyText>
            <Div
              data-testid="credentials-login-description-text"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </BodyText>

          <Gutter size="md" />
        </>
      )}

      {errorMessage && (
        <Div padding={{ bottom: 'md' }}>
          <InlineNotification
            data-testid="credentials-login-error-message"
            title={errorMessage}
            variant="warning"
          />
        </Div>
      )}

      <div className={styles.credentialsLogin_form}>
        <Input
          {...username}
          data-testid="credentials-login-username-input"
          value={username.value}
          label={username.label}
          name="username"
          type="text"
          onChange={(e) => username.onChange(e.target.value)}
          placeholder={username.label}
          autocomplete="off"
        />

        <Gutter size="md" />

        <Div className={styles.credentialsLogin_passwordContainer}>
          {password.tooltip && (
            <Tooltip
              overlayClassName={styles.credentialsLogin_passwordTooltip}
              trigger="click"
              placement="topCenter"
              tooltipContent={
                <BodyText tagName="span" size="xs" color="white">
                  {password.tooltip}
                </BodyText>
              }
            >
              <Icon
                className={styles.credentialsLogin_tooltipIcon}
                name="info"
                size="sm"
                color="purple500"
              />
            </Tooltip>
          )}

          <Input
            {...password}
            data-testid="credentials-login-password-input"
            value={password.value}
            name="password"
            label={password.label}
            type="password"
            onChange={(e) => password.onChange(e.target.value)}
            placeholder={password.label}
            autocomplete="off"
          />
        </Div>

        <Gutter size="lg" />

        <Button
          data-testid="credentials-login-button"
          variant="primary"
          onClick={() => {
            //  TODO: There is no way to set loading to false when an error occurs for example.
            setLoading(true);
            loginButton.onClick();
          }}
          size="md"
          iconName="chevron-right"
          iconOnRight={true}
          loading={loading}
        >
          {loginButton.label}
        </Button>

        {showInlineNotification && highlightedInfoText && (
          <>
            <Gutter size="lg" />

            <InlineNotification
              data-testid="credentials-login-highlighted-info-message"
              variant="information"
              closeOnClick={() => {
                setShowInlineNotification(false);
              }}
            >
              <Div
                className={styles.credentialsLogin_inlineNotification}
                dangerouslySetInnerHTML={{
                  __html: highlightedInfoText,
                }}
              />
            </InlineNotification>
          </>
        )}

        {(password.reset || strongAuthentication || register) && (
          <Gutter size="lg" />
        )}

        {password.reset && (
          <>
            <Link to={password.reset.url}>
              <Flex className={styles.credentialsLogin_iconUrlLinkContent}>
                {password.reset.url && (
                  <>
                    <Icon name="lock-closed" size="md" color="black" />

                    <Gutter size="xs" />
                  </>
                )}

                <BodyText tagName="span">{password.reset.label}</BodyText>
              </Flex>
            </Link>

            <Gutter size="md" />
          </>
        )}

        {register && (
          <>
            <Link to={register.url}>
              <Flex className={styles.credentialsLogin_iconUrlLinkContent}>
                {register.url && (
                  <>
                    <Icon name="register" size="md" color="black" />

                    <Gutter size="xs" />
                  </>
                )}

                <BodyText tagName="span">{register.label}</BodyText>
              </Flex>
            </Link>

            <Gutter size="md" />
          </>
        )}
      </div>
    </Div>
  );
};

export default CredentialsLogin;
